import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  NormalizedCacheObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { AppSyncRealTimeSubscriptionConfig } from 'aws-appsync-subscription-link/lib/types';
import { AuthOptions, createAuthLink, AUTH_TYPE } from 'aws-appsync-auth-link';
import { getAppSyncToken } from 'api/appsync';
import { getChatroomToken } from 'api';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { RetryLink } from '@apollo/client/link/retry';

interface Props {
  token?: string;
  children: React.ReactNode;
  eventId?: number;
}

export interface AppSyncProviderType {
  isClientReady: boolean;
  setIsClientReady: Dispatch<SetStateAction<boolean>>;
  setConnectedEvent: Dispatch<SetStateAction<number | undefined>>;
  client?: ApolloClient<NormalizedCacheObject>;
}

const url = process.env.APPSYNC_SOCKETS_API;
const region = process.env.APPSYNC_API_REGION;

const developer = process.env.DOMAIN != 'stagepilot.com';

export const AppSyncStateContext = createContext<AppSyncProviderType>(null!);

export function AppSyncProvider({ token, eventId, children }: Readonly<Props>) {
  const [client, setClient] = useState<
    ApolloClient<NormalizedCacheObject> | undefined
  >(undefined);

  useEffect(() => {
    if (eventId && !connectedEvent) {
      setConnectedEvent(eventId);
    }
  }, [eventId]);

  const [connectedEvent, setConnectedEvent] = useState<number | undefined>(
    eventId,
  );
  const [isClientReady, setIsClientReady] = useState(false);

  useEffect(() => {
    initializeClient();
  }, [token, connectedEvent]);

  async function fetchTokens(eventId: number, token?: string) {
    const chatToken = await fetchChatToken(eventId, token);
    const appSyncToken = await getAppSyncToken(eventId, chatToken);
    return { chatToken, appSyncToken };
  }

  function createApolloLinks(
    url: string,
    region: string,
    appSyncToken: string,
  ) {
    const auth: AuthOptions = {
      type: AUTH_TYPE.AWS_LAMBDA,
      token: appSyncToken,
    };

    const httpLink = new HttpLink({ uri: url });
    const handshakeLink = createCustomSubscriptionHandshakeLink(
      { url, region, auth },
      httpLink,
    );
    return ApolloLink.from([
      createAuthLink({ url, region, auth }),
      handshakeLink,
    ]);
  }

  async function initializeClient() {
    if (!url || !region || !connectedEvent) return;

    const { chatToken, appSyncToken } = await fetchTokens(
      connectedEvent,
      token,
    );
    if (!chatToken || !appSyncToken) return;
    const link = createApolloLinks(url, region, appSyncToken);
    if (developer) {
      loadDevMessages();
      loadErrorMessages();
    }

    const newClient = new ApolloClient({
      link,
      cache: new InMemoryCache(),
      connectToDevTools: developer,
    });

    if (newClient) {
      setClient(newClient);
      setIsClientReady(true);
    }
  }

  async function fetchChatToken(eventId: number, providedToken?: string) {
    return providedToken ?? (await getChatroomToken({ eventId }))?.token;
  }

  const createCustomSubscriptionHandshakeLink = (
    options: AppSyncRealTimeSubscriptionConfig,
    link: ApolloLink | undefined,
  ) => {
    const originalLink = createSubscriptionHandshakeLink(options, link);

    const retryLink = new RetryLink({
      delay: {
        initial: 1000,
        max: 3000,
        jitter: true,
      },
      attempts: {
        max: 5,
      },
    });
    const setControlMessagesLink = setContext(() => ({
      controlMessages: {
        '@@controlEvents': true,
      },
    }));

    return ApolloLink.from([setControlMessagesLink, retryLink, originalLink]);
  };

  const contextValue: AppSyncProviderType = useMemo(
    () => ({
      isClientReady,
      setIsClientReady,
      setConnectedEvent,
      client,
    }),
    [isClientReady, setIsClientReady, setConnectedEvent, client],
  );

  return (
    <AppSyncStateContext.Provider value={contextValue}>
      {client ? (
        <ApolloProvider client={client}>{children}</ApolloProvider>
      ) : (
        <>{children}</>
      )}
    </AppSyncStateContext.Provider>
  );
}
export const useAppSyncProvider = () => useContext(AppSyncStateContext);
